import type { FC } from 'react';
import { useCallback } from 'react';

import { L12nText } from '../../../../../../../text';
import { BMMDecision, BMMDecisionTypes } from '../../../../../gameInfo/results/treasureIsland/types';
import AdCircle from '../../assets/autoDecision/ad-circle.png';
import BMMEmeraldAutoDecision from '../../assets/BMM/autoDecision/EMERALD.png';
import BMMKeyEmeraldAutoDecision from '../../assets/BMM/autoDecision/KEY-EMERALD.png';
import BMMKeyRubyAutoDecision from '../../assets/BMM/autoDecision/KEY-RUBY.png';
import BMMKeyTopazAutoDecision from '../../assets/BMM/autoDecision/KEY-TOPAZ.png';
import BMMRubyAutoDecision from '../../assets/BMM/autoDecision/RUBY.png';
import BMMTopazAutoDecision from '../../assets/BMM/autoDecision/TOPAZ.png';
import BMMChestEmerald from '../../assets/BMM/CHEST-EMERALD.png';
import BMMChestRuby from '../../assets/BMM/CHEST-RUBY.png';
import BMMChestTopaz from '../../assets/BMM/CHEST-TOPAZ.png';
import BMMEmerald from '../../assets/BMM/EMERALD.png';
import BMMRuby from '../../assets/BMM/RUBY.png';
import BMMTopaz from '../../assets/BMM/TOPAZ.png';

import styles from './BMM.module.css';

enum BMM_TYPES {
  chest = 'CHEST',
  key = 'KEY',
}

const gemType: Record<string, string> = {
  '1': 'TOPASZ',
  '2': 'EMERALD',
  '3': 'RUBY',
  '4': 'CHEST',
};

const BMM_AUTO_ICONS: Record<string, string> = {
  ['KEY-EMERALD']: BMMKeyEmeraldAutoDecision,
  ['KEY-RUBY']: BMMKeyRubyAutoDecision,
  ['KEY-TOPAZ']: BMMKeyTopazAutoDecision,
  ['EMERALD']: BMMEmeraldAutoDecision,
  ['RUBY']: BMMRubyAutoDecision,
  ['TOPASZ']: BMMTopazAutoDecision,
};

const BMM_ICONS: Record<string, string> = {
  ['CHEST-EMERALD']: BMMChestEmerald,
  ['CHEST-RUBY']: BMMChestRuby,
  ['CHEST-TOPAZ']: BMMChestTopaz,
  ['EMERALD']: BMMEmerald,
  ['RUBY']: BMMRuby,
  ['TOPASZ']: BMMTopaz,
};

export const BMM: FC<{ barrels: BMMDecision[]; chestDetails: Record<string, string> }> = ({
  barrels,
  chestDetails,
}) => {
  const isAutoDecision = barrels.some((barrel) => barrel.autoDecision === 'true');

  const gemChest = gemType[chestDetails.chestType];

  const renderBarrel = useCallback(
    (playerDecision: BMMDecision, index: number) => {
      const gem = gemType[playerDecision.type];
      switch (gem) {
        case BMMDecisionTypes.emerald:
        case BMMDecisionTypes.ruby:
        case BMMDecisionTypes.topasz: {
          const mul = Number(playerDecision.mul) > 1 ? playerDecision.mul : '';
          return (
            <div className={styles.bmmBarrel} key={index}>
              {isAutoDecision && playerDecision.autoDecision === 'true' ? (
                <img src={BMM_AUTO_ICONS[gem]} className={styles.bmmBarrelGemAuto} alt={gem} />
              ) : (
                <img
                  className={`${mul ? styles.bmmBarrelGemBig : styles.bmmBarrelGemSmall}`}
                  src={BMM_ICONS[gem]}
                  alt={gem}
                />
              )}
              <span className={styles.bmmBarrelMultiplier}>{mul}</span>
            </div>
          );
        }

        case BMMDecisionTypes.chest:
          return (
            <div className={styles.bmmBarrel} key={index}>
              <img
                src={BMM_ICONS[`${BMM_TYPES.key}-${gemChest}`]}
                className={styles.bmmBarrelGemAuto}
                alt={`${gemChest} chest`}
              />
            </div>
          );

        default:
          return <div key={index}>{gem}</div>;
      }
    },
    [gemChest, isAutoDecision]
  );

  return (
    <div className={`${styles.contentContainerMargin} ${isAutoDecision ? styles.contentAutoContainer : ''}`}>
      <div className={styles.contentContainer}>
        {barrels.map(renderBarrel)}

        {chestDetails.chestFound === 'true' && (
          <div className={styles.bmmBarrel}>
            <img src={BMM_ICONS[`${BMM_TYPES.chest}-${gemChest}`]} alt={`${gemChest} chest`} />
            <span className={styles.bmmBarrelMultiplier}>{chestDetails.chestPrize}</span>
          </div>
        )}
      </div>

      {isAutoDecision && (
        <span className={styles.autoDecisionContainer}>
          <img src={AdCircle} alt="Auto decision applied" />
          <L12nText tk="AUTO_DECISIONS_APPLIED" />
        </span>
      )}
    </div>
  );
};
